import _ from 'lodash'
import { utils } from 'xlsx'

//************************************//
/**
 * Safely extracts human-readable text for a header cell (row 1).
 * Works across XLSX (where w or v exists) and CSV (where only v exists).
 *
 * @param {Object} cell - SheetJS cell object
 * @returns {string} Clean header text
 */
export const getCellHeader = (cell) => {
  if (!cell) return ''
  return (cell.w ?? cell.v ?? '').toString().trim()
}

//************************************//
/**
 * Safely extracts cell value for data rows (row 2+).
 * - For integer/decimal fields: preserves raw number if typeof cell.v === 'number' so validation passes.
 * - For dates / formatted fields: prefers formatted string cell.w (e.g. '2020-01-31' instead of 43861).
 * - Fallback to cell.v for CSV strings or unformatted cells.
 *
 * @param {Object} cell - SheetJS cell object
 * @param {Object} [field] - Custom field definition with field_type
 * @returns {string|number} Extracted cell value
 */
export const getCellValue = (cell, field = null) => {
  if (!cell) return ''

  const isNumericField = field?.field_type === 'integer' || field?.field_type === 'decimal'

  // For numeric fields, prefer numeric raw value so checkRules (_.isNumber) passes
  if (isNumericField && typeof cell.v === 'number') {
    return cell.v
  }

  // For dates and formatted text, prefer formatted string (.w)
  if (cell.w !== undefined && cell.w !== null && cell.w !== '') {
    return cell.w
  }

  // Fallback to raw value (.v) for CSV text or unformatted cells
  if (cell.v !== undefined && cell.v !== null) {
    return cell.v
  }

  return ''
}

//************************************//
/**
 * Extracts sorted list of column keys (e.g. ['A', 'B', 'C', ... 'AA', 'AB']) and row keys from worksheet,
 * avoiding metadata keys like !ref, !margins, !cols, !rows.
 *
 * @param {Object} sourceFile - SheetJS worksheet object
 * @returns {{ columns: string[], rows: string[] }}
 */
export const getSheetCoordinates = (sourceFile) => {
  if (sourceFile && sourceFile['!ref']) {
    try {
      const range = utils.decode_range(sourceFile['!ref'])
      const columns = []
      const rows = []
      for (let c = range.s.c; c <= range.e.c; ++c) {
        columns.push(utils.encode_col(c))
      }
      for (let r = range.s.r; r <= range.e.r; ++r) {
        rows.push((r + 1).toString())
      }
      return { columns, rows }
    } catch (e) {
      console.error('[getSheetCoordinates] decode_range failed, falling back to key extraction:', e)
    }
  }

  // Fallback if !ref is missing or malformed
  let columns = []
  let rows = []

  _.forEach(sourceFile, (x, index) => {
    if (typeof index === 'string' && !index.startsWith('!')) {
      const col = index.replace(/[0-9]/g, '')
      const row = index.replace(/\D/g, '')
      if (col) columns.push(col)
      if (row) rows.push(row)
    }
  })

  // Natural column sorting: length first, then alphabetical (A..Z, then AA..ZZ)
  columns = _.uniq(columns).sort((a, b) => {
    if (a.length !== b.length) return a.length - b.length
    return a.localeCompare(b)
  })

  // Numeric row sorting (1, 2, 3...)
  rows = _.uniq(rows).sort((a, b) => parseInt(a, 10) - parseInt(b, 10))

  return { columns, rows }
}
